import { useEffect, useMemo, useRef, useState } from "react";
import Swal from "../../../utils/apparifySwal";
import VariantBulkActionBar from "./VariantBulkActionBar";
import VariantFilterBar from "./VariantFilterBar";
import VariantIssuePanel from "./VariantIssuePanel";
import VariantTable from "./VariantTable";
import { buildSku, suggestPrefix, slug } from "./utils/sku";
import { businessSwalBase } from "../../utils/businessSwal";

function isBlank(v) {
  return v == null || String(v).trim() === "";
}

function toNumberOrNull(v) {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function makeIssueFilterMap(activeIssueFilters = []) {
  const map = {
    missingSku: false,
    invalidPrice: false,
    inactive: false,
  };
  activeIssueFilters.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(map, key)) map[key] = true;
  });
  return map;
}

function issueMatch(errs, issueFilterMap) {
  const anyActive = Object.values(issueFilterMap).some(Boolean);
  if (!anyActive) return true;

  return (
    (issueFilterMap.missingSku && errs.skuMissing) ||
    (issueFilterMap.invalidPrice && errs.priceInvalid) ||
    (issueFilterMap.inactive && errs.inactiveVariant)
  );
}

export default function VariantMatrix({
  variants,
  setVariants,
  productName,
  skuBase,
  setSkuBase,
  options = [],
  conflictingSkus = new Set(),
}) {
  const [selected, setSelected] = useState(new Set());
  const [toast, setToast] = useState(null); // {msg}
  const [activeFilters, setActiveFilters] = useState([]);
  const [activeIssueFilters, setActiveIssueFilters] = useState([]);
  const [issuePanelOpen, setIssuePanelOpen] = useState(false);
  const [skuToolsOpen, setSkuToolsOpen] = useState(true);
  const masterCheckboxRef = useRef(null);
  const swalBase = useMemo(() => ({ ...businessSwalBase, target: "#business-swal-root" }), []);

  const prefix = useMemo(() => suggestPrefix(skuBase || productName), [productName, skuBase]);
  const optionOrder = useMemo(
    () =>
      (options || [])
        .map((o) => (o?.name || "").trim())
        .filter(Boolean),
    [options]
  );

  const optionOrderSkuMismatchCount = useMemo(() => {
    if (!variants.length) return 0;
    return variants.filter((v) => {
      const current = String(v.sku || "").trim().toUpperCase();
      if (!current) return false;
      const expected = buildSku(prefix, v.optionValues, optionOrder).toUpperCase();
      return current !== expected;
    }).length;
  }, [variants, prefix, optionOrder]);

  // Detect stale SKU prefixes — SKUs that don't start with the current suggested prefix
  const staleSkuCount = useMemo(() => {
    if (!prefix) return 0;
    const upperPrefix = prefix.toUpperCase();
    return variants.filter((v) => {
      const sku = (v.sku || "").trim().toUpperCase();
      return sku && !sku.startsWith(upperPrefix);
    }).length;
  }, [variants, prefix]);

  const skuMap = useMemo(() => {
    const m = new Map();
    variants.forEach((v, i) => {
      const sku = (v.sku || "").trim().toUpperCase();
      if (!sku) return;
      m.set(sku, (m.get(sku) || []).concat(i));
    });
    return m;
  }, [variants]);

  const issues = useMemo(() => {
    return variants.map((v) => {
      const sku = (v.sku || "").trim();
      const priceN = toNumberOrNull(v.price);

      const errs = {
        skuMissing: isBlank(sku),
        skuDup: false,
        priceInvalid: priceN == null || priceN < 0,
        inactiveVariant: v.isActive === false,
      };

      if (!errs.skuMissing) {
        const key = sku.toUpperCase();
        const dup = skuMap.get(key);
        errs.skuDup = Array.isArray(dup) && dup.length > 1;
      }

      const count =
        (errs.skuMissing ? 1 : 0) +
        (errs.skuDup ? 1 : 0) +
        (errs.priceInvalid ? 1 : 0) +
        (errs.inactiveVariant ? 1 : 0);

      return { errs, count };
    });
  }, [variants, skuMap]);

  const issueCounts = useMemo(() => {
    return issues.reduce(
      (acc, row) => {
        if (row.errs.skuMissing) acc.missingSku += 1;
        if (row.errs.priceInvalid) acc.invalidPrice += 1;
        if (row.errs.inactiveVariant) acc.inactive += 1;
        return acc;
      },
      { missingSku: 0, invalidPrice: 0, inactive: 0 }
    );
  }, [issues]);

  const totalIssues =
    issueCounts.missingSku + issueCounts.invalidPrice + issueCounts.inactive;

  const filterGroups = useMemo(() => {
    const fromOptions = (options || [])
      .map((o) => ({
        name: (o?.name || "").trim(),
        values: Array.from(new Set((o?.values || []).map((v) => String(v).trim()).filter(Boolean))),
      }))
      .filter((o) => o.name && o.values.length > 0);

    if (fromOptions.length > 0) return fromOptions;

    const map = new Map();
    variants.forEach((v) => {
      Object.entries(v.optionValues || {}).forEach(([key, value]) => {
        if (!value) return;
        if (!map.has(key)) map.set(key, new Set());
        map.get(key).add(String(value));
      });
    });

    return Array.from(map.entries()).map(([name, values]) => ({
      name,
      values: Array.from(values).sort(),
    }));
  }, [options, variants]);

  const visibleIndices = useMemo(() => {
    const groupedFilters = activeFilters.reduce((acc, item) => {
      if (!acc[item.key]) acc[item.key] = new Set();
      acc[item.key].add(item.value);
      return acc;
    }, {});

    const issueFilterMap = makeIssueFilterMap(activeIssueFilters);

    return variants
      .map((v, i) => {
        const attrMatch = Object.entries(groupedFilters).every(([key, valuesSet]) =>
          valuesSet.has((v.optionValues || {})[key])
        );

        if (!attrMatch) return -1;
        if (!issueMatch(issues[i].errs, issueFilterMap)) return -1;
        return i;
      })
      .filter((i) => i !== -1);
  }, [variants, activeFilters, activeIssueFilters, issues]);

  const highlightedIssueRows = useMemo(() => {
    const set = new Set();
    const issueFilterMap = makeIssueFilterMap(activeIssueFilters);
    if (!Object.values(issueFilterMap).some(Boolean)) return set;

    issues.forEach((row, idx) => {
      if (issueMatch(row.errs, issueFilterMap)) set.add(idx);
    });

    return set;
  }, [issues, activeIssueFilters]);

  const selectedVisibleCount = useMemo(
    () => visibleIndices.filter((i) => selected.has(i)).length,
    [selected, visibleIndices]
  );

  const allVisibleSelected = visibleIndices.length > 0 && selectedVisibleCount === visibleIndices.length;

  useEffect(() => {
    if (!masterCheckboxRef.current) return;
    masterCheckboxRef.current.indeterminate = selectedVisibleCount > 0 && !allVisibleSelected;
  }, [allVisibleSelected, selectedVisibleCount]);

  function showToast(msg) {
    setToast({ msg });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 1600);
  }

  const toggle = (idx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const selectAll = () => {
    const allSelected = visibleIndices.every((i) => selected.has(i));
    if (allSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        visibleIndices.forEach((i) => next.delete(i));
        return next;
      });
    } else {
      setSelected((prev) => new Set([...prev, ...visibleIndices]));
    }
  };

  const update = (idx, patch) => {
    setVariants((prev) => prev.map((v, i) => (i !== idx ? v : { ...v, ...patch })));
  };

  const applyPatchToSelection = (patch) => {
    if (!selected.size) return;
    setVariants((prev) => prev.map((v, i) => (selected.has(i) ? { ...v, ...patch } : v)));
    showToast("Changes applied");
  };

  const autoSkuRow = (idx) => {
    setVariants((prev) =>
      prev.map((v, i) =>
        i !== idx ? v : { ...v, sku: buildSku(prefix, v.optionValues, optionOrder) }
      )
    );
    showToast("SKU generated");
  };

  const autoSkuSelectedOrAll = () => {
    setVariants((prev) =>
      prev.map((v, i) => {
        const should = selected.size ? selected.has(i) : true;
        if (!should) return v;
        const hasSku = v.sku?.trim();
        return hasSku ? v : { ...v, sku: buildSku(prefix, v.optionValues, optionOrder) };
      })
    );
    showToast(selected.size ? "SKUs auto-filled (selected)" : "SKUs auto-filled (all)");
  };

  const regenerateSkuSelectedOrAll = () => {
    // Prefix-only update: preserve existing suffix/order, only rewrite base segment.
    const applyBasePrefixOnly = (variant) => {
      const newBase = slug(prefix || "PROD");
      const current = String(variant?.sku || "").trim().toUpperCase();
      if (!current) return buildSku(newBase, variant?.optionValues, optionOrder);

      const optionSlugs = Object.values(variant?.optionValues || {})
        .map((v) => slug(v))
        .filter(Boolean);

      let suffixStart = -1;
      optionSlugs.forEach((token) => {
        const idx = current.indexOf(`-${token}`);
        if (idx > 0 && (suffixStart === -1 || idx < suffixStart)) suffixStart = idx;
      });

      let suffix = "";
      if (suffixStart > 0) {
        suffix = current.slice(suffixStart + 1);
      } else {
        const firstHyphen = current.indexOf("-");
        suffix = firstHyphen > 0 ? current.slice(firstHyphen + 1) : "";
      }

      return [newBase, suffix].filter(Boolean).join("-");
    };

    setVariants((prev) =>
      prev.map((v, i) => {
        const should = selected.size ? selected.has(i) : true;
        if (!should) return v;
        return { ...v, sku: applyBasePrefixOnly(v) };
      })
    );
    showToast(selected.size ? "SKU prefix updated (selected)" : "SKU prefix updated (all)");
  };

  const matchSkuToOptionOrder = async () => {
    const affected = selected.size ? selected.size : variants.length;
    if (!affected) return;
    const scope = selected.size ? "selected" : "all";

    const { isConfirmed } = await Swal.fire({
      ...swalBase,
      icon: "warning",
      title: "Standardize SKU format?",
      html: `This will reorder SKUs for <b>${affected} ${scope}</b> variant${affected !== 1 ? "s" : ""} to follow the current option sequence:<br/><b>${optionOrder.join(" → ") || "as shown"}</b>.<br/><br/>Existing SKU order will be replaced.`,
      showCancelButton: true,
      confirmButtonText: "Standardize",
      cancelButtonText: "Keep Current SKUs",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!isConfirmed) return;

    setVariants((prev) =>
      prev.map((v, i) => {
        const should = selected.size ? selected.has(i) : true;
        if (!should) return v;
        return { ...v, sku: buildSku(prefix, v.optionValues, optionOrder) };
      })
    );
    showToast(selected.size ? "SKU format standardized (selected)" : "SKU format standardized (all)");
  };

  const toggleVariantActive = async (idx) => {
    const v = variants[idx];
    if (!v) return;
    const willActivate = v.isActive === false;
    const result = await Swal.fire({
      ...swalBase,
      icon: willActivate ? "question" : "warning",
      title: willActivate ? "Activate Variant?" : "Deactivate Variant?",
      html: willActivate
        ? "This variant will be visible in your storefront and available for purchase."
        : "This variant will be hidden from your storefront and cannot be purchased.",
      showCancelButton: true,
      confirmButtonText: willActivate ? "Activate" : "Deactivate",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    update(idx, { isActive: willActivate });
    showToast(willActivate ? "Variant activated" : "Variant deactivated");
  };

  const toggleFilter = (key, value) => {
    setActiveFilters((prev) => {
      const exists = prev.some((x) => x.key === key && x.value === value);
      if (exists) return prev.filter((x) => !(x.key === key && x.value === value));
      return [...prev, { key, value }];
    });
  };

  const removeFilter = (key, value) => {
    setActiveFilters((prev) => prev.filter((x) => !(x.key === key && x.value === value)));
  };

  const toggleIssueFilter = (issueKey) => {
    setActiveIssueFilters((prev) =>
      prev.includes(issueKey) ? prev.filter((x) => x !== issueKey) : [...prev, issueKey]
    );
  };

  const openIssueView = () => {
    if (activeIssueFilters.length === 0) {
      const defaults = [];
      if (issueCounts.missingSku > 0) defaults.push("missingSku");
      if (issueCounts.invalidPrice > 0) defaults.push("invalidPrice");
      if (issueCounts.inactive > 0) defaults.push("inactive");
      setActiveIssueFilters(defaults);
    }
    setIssuePanelOpen(true);
  };

  const previewSku = useMemo(() => {
    if (variants.length > 0) return buildSku(prefix, variants[0].optionValues, optionOrder);

    const sample = {};
    (options || []).forEach((opt) => {
      const name = (opt?.name || "").trim();
      const firstValue = (opt?.values || [])[0];
      if (!name || !firstValue) return;
      sample[name] = firstValue;
    });

    return buildSku(prefix, sample, optionOrder);
  }, [variants, options, prefix, optionOrder]);

  const copySku = async (sku) => {
    if (!sku) return showToast("SKU is empty");
    const ok = await copyToClipboard(sku.toUpperCase());
    showToast(ok ? "SKU copied" : "Copy failed");
  };

  const inputBase =
    "rounded-xl border px-3 py-2 outline-none " +
    "focus:border-zinc-400 focus:ring-2 focus:ring-zinc-200 " +
    "dark:focus:border-zinc-600 dark:focus:ring-zinc-800 " +
    "dark:bg-[#0f0f10]";

  return (
    <div className="space-y-2.5">
      {/* toast */}
      {toast && (
        <div className="fixed inset-x-3 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-[60] flex justify-center sm:inset-x-auto sm:right-4">
          <div className="rounded-2xl border border-zinc-200 bg-white/95 px-4 py-3 text-sm shadow-lg backdrop-blur
                          text-zinc-900 dark:border-zinc-800 dark:bg-[#121214]/95 dark:text-zinc-100">
            {toast.msg}
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-[#121214]">
        <button
          type="button"
          onClick={() => setSkuToolsOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-4 px-4 py-2.5 text-left transition hover:bg-zinc-50/80 dark:hover:bg-zinc-900/30"
        >
          <div>
            <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">SKU Setup</div>
            <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
              Keep SKU naming consistent while options evolve.
            </div>
          </div>
          <span className="text-xs text-zinc-500 dark:text-zinc-400">{skuToolsOpen ? "Hide" : "Show"}</span>
        </button>

        {skuToolsOpen && (
          <div className="space-y-2.5 border-t border-zinc-200 bg-zinc-50/40 p-3 dark:border-zinc-800 dark:bg-black/10">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  value={skuBase || ""}
                  onChange={(e) => setSkuBase?.(e.target.value)}
                  placeholder="SKU prefix, e.g. NEWBUZZER or NBFS"
                  className={inputBase + " w-full sm:min-w-[220px] border-zinc-200 bg-white text-zinc-900 dark:border-zinc-800 dark:text-zinc-100"}
                />
                <div className="text-xs text-zinc-500 dark:text-zinc-400">
                  Current prefix: <span className="font-semibold text-zinc-700 dark:text-zinc-200">{prefix || "PROD"}</span>
                </div>
              </div>

              <div className="text-xs text-zinc-500 dark:text-zinc-400">
                Preview: <span className="font-semibold text-zinc-700 dark:text-zinc-200">{previewSku || "PROD-SAMPLE"}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={autoSkuSelectedOrAll}
                className="rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-zinc-50
                           dark:border-zinc-800 dark:bg-[#121214] dark:text-zinc-100 dark:hover:bg-zinc-900/60"
                title="Generate SKUs only for rows where SKU is empty."
              >
                Auto-fill SKUs {selected.size ? "(Selected)" : "(All)"}
              </button>

              <button
                type="button"
                onClick={regenerateSkuSelectedOrAll}
                className="rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-zinc-50
                           dark:border-zinc-800 dark:bg-[#121214] dark:text-zinc-100 dark:hover:bg-zinc-900/60"
                title="Only updates the front part of the SKU. Existing suffix stays the same."
              >
                Set SKU Prefix {selected.size ? "(Selected)" : "(All)"}
              </button>

              <button
                type="button"
                onClick={matchSkuToOptionOrder}
                className="rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-zinc-50
                           dark:border-zinc-800 dark:bg-[#121214] dark:text-zinc-100 dark:hover:bg-zinc-900/60"
                title="Rewrites SKU option sequence to match current option order."
              >
                Standardize SKU Format {selected.size ? "(Selected)" : "(All)"}
              </button>
            </div>

            <div className="flex flex-wrap gap-2 text-[11px] text-zinc-500 dark:text-zinc-400">
              {staleSkuCount > 0 && <span>{staleSkuCount} SKU(s) use an old prefix.</span>}
              {optionOrderSkuMismatchCount > 0 && <span>{optionOrderSkuMismatchCount} SKU(s) need format standardization.</span>}
              {staleSkuCount === 0 && optionOrderSkuMismatchCount === 0 && <span>All SKUs are aligned with current naming settings.</span>}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-[#121214]">
        <VariantFilterBar
          groups={filterGroups}
          activeFilters={activeFilters}
          onToggleFilter={toggleFilter}
          onRemoveFilter={removeFilter}
          onClear={() => setActiveFilters([])}
          visibleCount={visibleIndices.length}
          totalCount={variants.length}
          integrated
        />

        {(selected.size > 0 || totalIssues > 0) && (
          <div className="border-b border-zinc-200 p-2 dark:border-zinc-800">
            <VariantBulkActionBar
              selectedCount={selected.size}
              totalIssues={totalIssues}
              issueCounts={issueCounts}
              onViewIssues={openIssueView}
              onApplyPatch={applyPatchToSelection}
              onApplyStatus={(isActive) => applyPatchToSelection({ isActive })}
            />
          </div>
        )}

        <VariantTable
          variants={variants}
          visibleIndices={visibleIndices}
          selected={selected}
          masterCheckboxRef={masterCheckboxRef}
          allVisibleSelected={allVisibleSelected}
          onSelectAll={selectAll}
          onToggle={toggle}
          onUpdate={update}
          onAutoFillRowSku={autoSkuRow}
          onCopySku={copySku}
          onToggleActive={toggleVariantActive}
          options={options}
          issues={issues}
          highlightedIssueRows={highlightedIssueRows}
          conflictingSkus={conflictingSkus}
          inputBaseClass={inputBase}
          integrated
        />
      </div>

      <VariantIssuePanel
        open={issuePanelOpen}
        counts={issueCounts}
        activeFilters={activeIssueFilters}
        onToggle={toggleIssueFilter}
        onSelectAll={() => setActiveIssueFilters(["missingSku", "invalidPrice", "inactive"])}
        onClear={() => setActiveIssueFilters([])}
        onClose={() => setIssuePanelOpen(false)}
      />

      <div className="text-xs text-zinc-500 dark:text-zinc-400">
        Variants are generated from your selected options. Use the toggle to enable or disable individual variants — you cannot manually add or remove combinations.
      </div>

      <div className="text-xs text-zinc-500 dark:text-zinc-400">
        Tip: use <span className="font-semibold">Set SKU Prefix</span> when naming changes, and <span className="font-semibold">Standardize SKU Format</span> after option reordering.
      </div>
    </div>
  );
}
